// @ts-nocheck
import {
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormCheck,
  CFormInput,
  CFormTextarea,
  CModal,
  CSpinner,
} from "@coreui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import { Field, FormSection } from "../../../components/ui/FormUi";
import RenderTable from "../../../components/ui/RenderTable";
import { createZoneAwareDate } from "../../../helpers/general";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import { cardStyle } from "../../Places/PlacesTable"; // adjust path
import RestrictedRoute from "../../auth/RestrictedRoute";
import CategoryModel from "./CategoryModel";
import NotificationTargets from "./NotificationTargets";
import UsersDisplayTable, { DisplayUser } from "./UsersDisplayTable";
import UsersModel from "./UsersModel";
import { SwitchCard } from "../../../components/ui/ToggleUi";

enum SendNotificationType {
  NOW = "now",
  CUSTOM = "custom",
}

enum NotificationUserType {
  CATEGORY = "category",
  USERS = "users",
}

const TARGETS = [
  { value: "place", label: "Établissements" },
  { value: "offer", label: "Offres" },
  { value: "event", label: "Événements" },
  { value: "none", label: "Aucun" },
];

const SEND_TYPES = [
  { value: "now", label: "Immédiatement" },
  { value: "custom", label: "Personnalisé" },
];

const userItemSchema = yup.object({
  id: yup.string().required("Ce champ est obligatoire"),
  displayValue: yup.string().required("Ce champ est obligatoire"),
  profilePicture: yup.string().nullable(),
  email: yup.string().nullable(),
});

const usersSchema = yup.object({
  type: yup.string().required("Ce champ est obligatoire"),
  items: yup.array().of(userItemSchema).min(1, "Ce champ est obligatoire"),
});

const schema = yup.object().shape({
  title: yup.string().required("Ce champ est obligatoire"),
  description: yup.string().nullable(),
  sendType: yup.string().required("Ce champ est obligatoire"),
  sendToAllUsers: yup.string().nullable(),
  target: yup.string().required("Ce champ est obligatoire").default("place"),
  targetId: yup
    .string()
    .nullable()
    .when("target", {
      is: "none",
      then: () => yup.string().nullable(),
      otherwise: () => yup.string().required("Ce champ est obligatoire"),
    }),
  date: yup
    .string()
    .nullable()
    .when("sendType", {
      is: "custom",
      then: () =>
        yup
          .date()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
    }),
  users: yup
    .array()
    .ensure()
    .when("sendToAllUsers", {
      is: "false",
      then: () =>
        yup.array().of(usersSchema).min(1, "Ce champ est obligatoire"),
      otherwise: () => yup.array().of(usersSchema).min(0),
    }),
});

export default function NewNotification() {
  const [formLoading, setFormLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [categoryModelVisible, setCategoryModelVisible] = useState(false);
  const [usersModelVisible, setUsersModelVisible] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control: formControl,
    watch: watchFormValue,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { target: "place" },
  });

  const createNotiticationMutation = useMutation((data: any) =>
    apiService.MakePostRequest("notifications", data, token),
  );

  const {
    fields: usersFields,
    append: appendUsersField,
    remove: removeUsersField,
  } = useFieldArray({
    control: formControl,
    name: "users",
  });

  useEffect(() => {
    apiService.MakeGetRequest("categories").then((res) => setCategories(res));
  }, []);

  const deleteUsersField = (item: any, index: number) => {
    if (item.type === NotificationUserType.CATEGORY) {
      setSelectedCategories((current) =>
        current.filter((cat) => cat.id !== item.items?.[0].id),
      );
    }
    removeUsersField(index);
  };

  const deleteUserField = (userId: string) => {
    const userField = usersFields.find(
      (item: any) => item.type === NotificationUserType.USERS,
    );
    if (userField) {
      const userIndex = userField?.items?.findIndex(
        (user: any) => user.id === userId,
      );
      if (userIndex !== -1) {
        const users = userField?.items?.filter(
          (user: any) => user.id !== userId,
        );
        removeUsersField(usersFields.indexOf(userField));
        appendUsersField({
          type: NotificationUserType.USERS,
          items: users,
        });
        setSelectedUsers(users);
      }
    }
  };

  const target = watchFormValue("target");
  const isCustomDate =
    watchFormValue("sendType") === SendNotificationType.CUSTOM;
  const isSendToAllUsers = watchFormValue("sendToAllUsers");

  const onSubmit = async (dataArg: any) => {
    const data = { ...dataArg };
    try {
      setFormLoading(true);
      const notificationUsers: string[] = [];
      const notificationCategories: string[] = [];
      data.users.forEach((item: any) => {
        if (item.type === NotificationUserType.CATEGORY) {
          notificationCategories.push(item.items?.[0].id);
        } else if (item.type === NotificationUserType.USERS) {
          notificationUsers.push(...item.items.map((user: any) => user.id));
        }
      });
      const timezonedDate = createZoneAwareDate(data.date);
      const payload = {
        title: data.title,
        text: data.description,
        target: data.target,
        targetId: data.targetId,
        ...(data.sendType === SendNotificationType.NOW
          ? { sendNow: true }
          : { date: timezonedDate }),
        sendToAllUsers: data.sendToAllUsers,
        users: data.sendToAllUsers === "true" ? [] : notificationUsers,
        categories: notificationCategories,
      };
      await createNotiticationMutation.mutateAsync(payload);
      toastSuccess(general.fr.message.notificationCreated);
      navigate("/notifications");
    } catch (error) {
      setFormLoading(false);
      console.log(error);
      toastError(general.fr.message.operationFailed);
    }
  };

  return (
    <RestrictedRoute>
      <CModal visible={categoryModelVisible}>
        <CategoryModel
          categories={categories.filter(
            (cat) =>
              !selectedCategories.find((selected) => selected.id === cat.id),
          )}
          onSelect={(category) => {
            appendUsersField({
              type: NotificationUserType.CATEGORY,
              items: [{ id: category.id, displayValue: category.label }],
            });
            setSelectedCategories((current) => [...current, category]);
            setCategoryModelVisible(false);
          }}
          onClose={() => setCategoryModelVisible(false)}
        />
      </CModal>
      <CModal visible={usersModelVisible} size="lg">
        <UsersModel
          selectedUsers={selectedUsers}
          onSelect={(users) => {
            const prvUsersField = usersFields.find(
              (item: any) => item.type === NotificationUserType.USERS,
            );
            if (prvUsersField) {
              removeUsersField(usersFields.indexOf(prvUsersField));
            }
            appendUsersField({
              type: NotificationUserType.USERS,
              items: users?.map((user) => ({
                id: user.id,
                displayValue: user.fullName,
                profilePicture: user?.profilePicture,
                email: user.email,
              })),
            });
            setSelectedUsers(users);
            setUsersModelVisible(false);
          }}
          onClose={() => setUsersModelVisible(false)}
        />
      </CModal>

      <div className="vz-form px-2 mt-4">
        {/* Header */}
        <div
          style={{ ...cardStyle, padding: "16px 20px" }}
          className="d-flex justify-content-between align-items-center mb-3"
        >
          <div>
            <h4 className="fs-4 fw-bold mb-0">Ajouter une notification</h4>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Les champs marqués d'une * sont obligatoires
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            className="btn btn-outline-secondary rounded-pill px-3"
          >
            ← Retour
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Content */}
          <FormSection title="Contenu">
            <Field
              label="Titre"
              htmlFor="title"
              required
              error={errors.title?.message}
            >
              <CFormInput
                id="title"
                placeholder="Titre de la notification"
                {...register("title")}
              />
            </Field>
            <Field
              label="Description"
              htmlFor="description"
              error={errors.description?.message}
            >
              <CFormTextarea
                id="description"
                rows={3}
                placeholder="Description de la notification"
                {...register("description")}
              />
            </Field>
          </FormSection>

          {/* Target */}
          <FormSection title="Cible">
            <Field
              col="col-12"
              label="Cible de notification"
              required
              error={errors.target?.message}
            >
              <Controller
                name="target"
                control={formControl}
                render={({ field }) => (
                  <div className="d-flex flex-wrap gap-4 pt-1">
                    {TARGETS.map((t) => (
                      <CFormCheck
                        key={t.value}
                        type="radio"
                        id={`target_${t.value}`}
                        label={t.label}
                        value={t.value}
                        checked={field.value === t.value}
                        onChange={() => field.onChange(t.value)}
                      />
                    ))}
                  </div>
                )}
              />
            </Field>

            {target !== "none" && (
              <Field
                col="col-12"
                label="Choisissez une cible"
                required
                error={errors.targetId?.message}
              >
                <NotificationTargets
                  target={target}
                  onChange={(val: string) => setValue("targetId", val)}
                />
              </Field>
            )}
          </FormSection>

          {/* Schedule */}
          <FormSection title="Envoi">
            <Field
              label="Quand envoyer ?"
              required
              error={errors.sendType?.message}
            >
              <Controller
                name="sendType"
                control={formControl}
                render={({ field }) => (
                  <div className="d-flex flex-wrap gap-4 pt-1">
                    {SEND_TYPES.map((t) => (
                      <CFormCheck
                        key={t.value}
                        type="radio"
                        id={`sendType_${t.value}`}
                        label={t.label}
                        value={t.value}
                        checked={field.value === t.value}
                        onChange={() => field.onChange(t.value)}
                      />
                    ))}
                  </div>
                )}
              />
            </Field>

            {isCustomDate && (
              <Field
                label="Date et heure"
                htmlFor="date"
                required
                error={errors.date?.message}
              >
                <CFormInput
                  id="date"
                  type="datetime-local"
                  {...register("date")}
                />
              </Field>
            )}
          </FormSection>

          {/* Recipients */}
          <section style={cardStyle} className="mb-3">
            <div
              className="d-flex justify-content-between align-items-center mb-3 pb-2"
              style={{ borderBottom: "1px solid #f0f0f0" }}
            >
              <div>
                <h6 className="fw-bold mb-0" style={{ fontSize: 15 }}>
                  Utilisateurs <span className="text-danger">*</span>
                </h6>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Choisissez les destinataires de la notification
                </div>
              </div>
              <CDropdown>
                <CDropdownToggle
                  color="primary"
                  className="btn-sm rounded-pill px-3 text-white"
                  disabled={!!isSendToAllUsers}
                >
                  + Ajouter des utilisateurs
                </CDropdownToggle>
                <CDropdownMenu>
                  <CDropdownItem
                    className="m-0 text-black w-full p-2"
                    onClick={() => setCategoryModelVisible(true)}
                  >
                    Utilisateurs d'une catégorie
                  </CDropdownItem>
                  <CDropdownItem
                    className="m-0 text-black w-full p-2"
                    onClick={() => setUsersModelVisible(true)}
                  >
                    Sélectionnez les utilisateurs
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </div>

            <div className="mb-3">
              <SwitchCard
                id="sendToAllUsers"
                label="Envoyer à tous les utilisateurs"
                {...register("sendToAllUsers")}
              />
            </div>
            {errors.users?.message && (
              <div style={{ color: "#dc3545", fontSize: 12 }} className="mb-2">
                {errors.users.message}
              </div>
            )}

            {usersFields.length === 0 && !isSendToAllUsers && (
              <div
                className="text-center text-muted py-3"
                style={{
                  border: "1px dashed #ddd",
                  borderRadius: 12,
                  fontSize: 13,
                }}
              >
                Aucun destinataire sélectionné
              </div>
            )}

            <div className="d-flex flex-column gap-3">
              {usersFields.map((item, index) => {
                if (item.type === NotificationUserType.CATEGORY) {
                  return (
                    <div
                      key={item.id}
                      className="d-flex align-items-center justify-content-between gap-3 p-3"
                      style={{
                        border: "1px solid #eee",
                        borderRadius: 12,
                        backgroundColor: "#fcfcfc",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="d-inline-flex align-items-center rounded-pill"
                          style={{
                            height: 22,
                            padding: "0 9px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#f0813f",
                            backgroundColor: "rgba(240,129,63,0.14)",
                          }}
                        >
                          Catégorie
                        </span>
                        <span className="fw-semibold">
                          {item.items?.[0].displayValue}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-pill px-3"
                        onClick={() => deleteUsersField(item, index)}
                        title="Retirer"
                      >
                        <DeleteIcon width="16" height="16" />
                      </button>
                    </div>
                  );
                }
                if (
                  item.type === NotificationUserType.USERS &&
                  item?.items?.length
                ) {
                  return (
                    <div
                      key={item.id}
                      className="p-3"
                      style={{
                        border: "1px solid #eee",
                        borderRadius: 12,
                        backgroundColor: "#fcfcfc",
                      }}
                    >
                      <div
                        className="text-muted mb-2"
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Utilisateurs sélectionnés ({item.items.length})
                      </div>
                      <RenderTable
                        loading={false}
                        render={() => (
                          <UsersDisplayTable
                            users={item.items as DisplayUser[]}
                            deleteUser={deleteUserField}
                          />
                        )}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </section>

          {/* Sticky actions */}
          <div
            className="d-flex justify-content-end gap-2 py-3 px-3 mt-3"
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              backgroundColor: "#fff",
              borderRadius: 16,
              boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
            }}
          >
            <button
              type="button"
              disabled={formLoading}
              onClick={() => navigate("/notifications")}
              className="btn btn-outline-secondary rounded-pill px-4"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="btn btn-success text-white rounded-pill px-4"
            >
              {formLoading ? <CSpinner size="sm" /> : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </RestrictedRoute>
  );
}
